import type { ToolCall, ToolCallStatus } from '@kanbu/schema';
import { ToolType } from '@kanbu/schema/enums';
import type { StreamStateStatus } from '@kanbu/shared';
import { type ReactNode, useMemo } from 'react';
import type { z } from 'zod';

import { ToolActivity } from './components/tool-activity';
import type { ToolDefinition, ToolRegistry } from './tool-registry';

/**
 * Renders a tool call that has no dedicated component in the registry. Opt-in
 * (admin inbox only); when unset, unknown tools are not rendered.
 */
export type RenderUnknownTool = (props: {
  toolName: string;
  toolCallId: string;
  args: unknown;
  content?: unknown;
  artifact?: unknown;
  toolCallStatus?: ToolCallStatus;
  kind?: string;
  state: ToolState;
  error?: string;
}) => ReactNode;

/**
 * Represents the possible states of a tool throughout its lifecycle
 */
export type ToolState =
  | 'call' // Tool call generation started (LLM defining the tool call)
  | 'call-partial' // Tool is being defined, args are streaming
  | 'call-complete' // Tool definition is complete with full args and starts it's execution
  | 'result' // Tool has been executed with result
  | 'error'; // Error occurred during any phase

/**
 * Props for the tool component. All components
 * registered to the tool registry will receive
 * these props.
 */
export type ToolComponentProps<
  ToolCallType extends Record<string, any> = any,
  Content = unknown,
  Artifact = unknown,
  Props = {},
> = Props & {
  toolCall: ToolCall<ToolCallType>;
  state: ToolState;
  content?: Content;
  artifact?: Artifact;
  error?: string;
  parseError?: z.ZodError;
};

/**
 * Props for the tool provider component.
 */
export interface ToolProviderProps {
  toolName: string;
  toolCallId: string;
  args: unknown;
  content: unknown;
  artifact?: unknown;
  status: StreamStateStatus | undefined;

  /**
   * Explicit lifecycle of client-side tool calls awaiting user input.
   */
  toolCallStatus?: ToolCallStatus;
  toolRegistry: ToolRegistry;
  index: number;
  error?: string;
  renderFallback?: RenderUnknownTool;
  /**
   * Model-authored activity status ("Searching the knowledge base…").
   * Rendered as a customer-facing activity line for tools that have no
   * dedicated component (and no fallback renderer).
   */
  label?: string;
}

function hasToolOutput(content?: unknown, artifact?: unknown): boolean {
  if (content !== undefined && content !== null && content !== '') {
    return true;
  }

  return artifact !== undefined && artifact !== null;
}

function getToolState(
  status: StreamStateStatus | undefined,
  content?: unknown,
  hasError?: boolean,
  toolCallStatus?: ToolCallStatus,
  artifact?: unknown,
): ToolState {
  if (hasError || toolCallStatus === 'error') {
    return 'error';
  }

  /**
   * Recorded output wins over a stale `pending` flag. Orphan card tools can
   * keep `pending` after the tools-channel finish never matched; once content
   * or an artifact is present the UI must show the result, not a spinner.
   */
  if (hasToolOutput(content, artifact) || toolCallStatus === 'completed') {
    return 'result';
  }

  // Awaiting user input — the call is complete, no result yet
  if (toolCallStatus === 'pending') {
    return 'call-complete';
  }

  switch (status) {
    case 'start':
      return 'call';

    case 'streaming':
      return 'call-partial';

    case 'end':
    case 'interrupted':
      return 'call-complete';

    case 'error':
      return 'error';

    default:
      /**
       * No stream status means the message was loaded from persistence
       * (page reload, admin inbox) — the run is over, so a tool call
       * without recorded content is settled, never "running".
       */
      return 'call-complete';
  }
}

/**
 * Helper to parse errors with schema.
 */
function parseWithSchema<T>(
  value: unknown,
  schema?: z.ZodType<T>,
): { data: unknown; error?: z.ZodError } {
  if (!schema || value === undefined) {
    return { data: value };
  }

  try {
    const result = schema.safeParse(value);

    if (result.success) {
      return { data: result.data };
    }

    console.error('Schema parsing error:', result.error);

    return { data: value, error: result.error };
  } catch (e) {
    console.error('Schema parsing error:', e);

    return { data: value };
  }
}

/**
 * Tool provider, renders the tool component with
 * the given state and result.
 */
export function ToolProvider({
  toolName,
  toolCallId,
  args,
  content,
  artifact,
  error: initialError,
  status,
  toolCallStatus,
  toolRegistry,
  index = 0,
  renderFallback,
  label,
}: ToolProviderProps) {
  // Find the tool definition in registry
  const toolDef =
    toolRegistry[toolName] ?? ({} as ToolDefinition<any, any, any>);
  const { validate, schema, onParseError } = toolDef;

  /**
   * Parse args, content, and artifact using schemas to make;
   * sure the data are valid.
   */
  const parsedArgs = useMemo(
    () => (validate ? parseWithSchema(args, schema?.args) : { data: args }),
    [args, schema?.args, validate],
  );

  const parsedContent = useMemo(
    () =>
      validate ? parseWithSchema(content, schema?.content) : { data: content },
    [content, schema?.content, validate],
  );

  const parsedArtifact = useMemo(
    () =>
      validate
        ? parseWithSchema(artifact, schema?.artifact)
        : { data: artifact },
    [artifact, schema?.artifact, validate],
  );

  // Generate parsing error message if needed
  const parseError = useMemo(() => {
    if (!validate) {
      return;
    }

    const errors = [
      parsedArgs.error,
      parsedContent.error,
      parsedArtifact.error,
    ].filter(Boolean);

    if (errors.length === 0) {
      return;
    }

    if (onParseError) {
      return errors.map(error => onParseError!(error!)).join('\n');
    }

    // Default error message
    return 'Failed to parse tool response data';
  }, [
    validate,
    parsedArgs.error,
    parsedContent.error,
    parsedArtifact.error,
    onParseError,
  ]);

  // Create a standardized tool call object
  const toolCall: ToolCall<any> = useMemo(
    () => ({
      id: toolCallId,
      type: ToolType.Tool,
      name: toolName,
      args: parsedArgs.data,
      index,
      content: parsedContent.data,
      artifact: parsedArtifact.data,
      status: toolCallStatus,
    }),
    [
      toolCallId,
      toolName,
      parsedArgs.data,
      index,
      parsedContent.data,
      parsedArtifact.data,
      toolCallStatus,
    ],
  );

  // Determine final state - error state takes precedence
  const hasError = Boolean(initialError || parseError);
  const finalState = getToolState(
    status,
    content,
    hasError,
    toolCallStatus,
    artifact,
  );
  const error = initialError || parseError;

  // Unknown tool (no registry component): try the opt-in fallback renderer
  // first (admin inspector, client-side form cards). Fallbacks return null
  // for tools they don't handle — then a model-authored activity label
  // renders as a customer-facing status line; otherwise nothing.
  if (!Object.keys(toolDef).length) {
    const fallback = renderFallback?.({
      toolName,
      toolCallId,
      args,
      content,
      artifact,
      toolCallStatus,
      state: finalState,
      error,
    });

    if (fallback !== null && fallback !== undefined && fallback !== false) {
      return fallback;
    }

    return label ? <ToolActivity label={label} state={finalState} /> : null;
  }

  const { component: ToolComponent } = toolDef;

  return (
    <ToolComponent
      toolCall={toolCall}
      state={finalState}
      content={parsedContent.data}
      artifact={parsedArtifact.data}
      error={error}
      parseError={
        parsedArgs.error || parsedContent.error || parsedArtifact.error
      }
    />
  );
}
